<template>
  <div class="flex justify-start">
    <div class="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-600 max-w-[85%] min-w-[60%]">
      <div v-if="awaitingConfirmation" class="flex items-center gap-2">
        <Icon name="heroicons:hand-raised" class="w-4 h-4 flex-shrink-0" />
        <span>{{ toolLabel }} &mdash; awaiting confirmation</span>
      </div>

      <div
        v-else-if="part.state === 'input-streaming' || part.state === 'input-available'"
        class="flex items-center gap-2"
      >
        <Icon name="heroicons:arrow-path" class="w-4 h-4 animate-spin flex-shrink-0" />
        <span>Working: {{ toolLabel }}&hellip;</span>
      </div>

      <details v-else-if="part.state === 'output-available'">
        <summary class="flex items-center gap-2 cursor-pointer list-none">
          <Icon name="heroicons:check-circle" class="w-4 h-4 text-green-500 flex-shrink-0" />
          <span>Done: {{ toolLabel }}</span>
          <Icon name="heroicons:chevron-down" class="w-3 h-3 text-gray-400" />
        </summary>
        <pre
          class="mt-2 max-h-48 overflow-auto bg-gray-100 rounded p-2 whitespace-pre-wrap break-all"
        >{{ detailsJson }}</pre>
      </details>

      <div v-else-if="part.state === 'output-error'" class="flex items-center gap-2 text-red-500">
        <Icon name="heroicons:exclamation-circle" class="w-4 h-4 flex-shrink-0" />
        <span>Failed: {{ toolLabel }}<template v-if="part.errorText"> &mdash; {{ part.errorText }}</template></span>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
const props = defineProps<{
  part: any;
}>();

const agentStore = useAgentStore();

// A confirmation-gated call stays in 'input-available' until it gets an output;
// while it sits in the confirmation queue, label it as paused rather than working
const awaitingConfirmation = computed(
  () =>
    props.part?.state === 'input-available' &&
    (agentStore.pendingConfirmations ?? []).some((p: any) => p.toolCall.toolCallId === props.part?.toolCallId)
);

const toolName = computed<string>(() => {
  if (props.part?.type === 'dynamic-tool') return props.part?.toolName ?? 'tool';
  return String(props.part?.type ?? '').replace(/^tool-/, '') || 'tool';
});

// User-facing label: app terminology says "assistance request(s)", never "case(s)"
const toolLabel = computed(() =>
  toolName.value.replace(/_/g, ' ').replace(/\bcases\b/g, 'assistance requests').replace(/\bcase\b/g, 'assistance request')
);

const detailsJson = computed(() => {
  try {
    return JSON.stringify({ input: props.part?.input, output: props.part?.output }, null, 2);
  } catch {
    return '';
  }
});
</script>
